import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  LinearProgress,
  Typography,
  Box,
  CircularProgress,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { Close } from "@material-ui/icons";

const useStyles = makeStyles((theme) => ({
  dialog: {
    "& .MuiDialog-paper": {
      minWidth: "400px",
      borderRadius: "12px",
      padding: theme.spacing(2),
    },
  },
  title: {
    textAlign: "center",
    color: "#f44336",
    fontWeight: "bold",
    marginBottom: theme.spacing(2),
  },
  content: {
    textAlign: "center",
    padding: theme.spacing(2),
  },
  progressContainer: {
    margin: theme.spacing(3, 0),
  },
  progressBar: {
    height: "8px",
    borderRadius: "4px",
    backgroundColor: "#e0e0e0",
    "& .MuiLinearProgress-bar": {
      borderRadius: "4px",
      backgroundColor: "#f44336",
    },
  },
  progressText: {
    marginTop: theme.spacing(1),
    fontSize: "14px",
    color: theme.palette.text.secondary,
  },
  statusText: {
    marginTop: theme.spacing(2),
    fontSize: "16px",
    fontWeight: "500",
    color: theme.palette.text.primary,
  },
  iconContainer: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    marginBottom: theme.spacing(2),
  },
  disconnectIcon: {
    fontSize: "48px",
    color: "#f44336",
    animation: "$pulse 1.5s ease-in-out infinite",
  },
  "@keyframes pulse": {
    "0%": {
      transform: "scale(1)",
      opacity: 1,
    },
    "50%": {
      transform: "scale(1.1)",
      opacity: 0.7,
    },
    "100%": {
      transform: "scale(1)",
      opacity: 1,
    },
  },
  successIcon: {
    fontSize: "48px",
    color: "#4caf50",
  },
  errorIcon: {
    fontSize: "48px",
    color: "#f44336",
  },
  actions: {
    justifyContent: "center",
    padding: theme.spacing(2),
  },
  closeButton: {
    backgroundColor: "#f44336",
    color: "white",
    "&:hover": {
      backgroundColor: "#d32f2f",
    },
  },
}));

const DisconnectProgressModal = ({ 
  open, 
  onClose, 
  whatsAppName = "WhatsApp"
}) => {
  const classes = useStyles();
  const [progress, setProgress] = useState(0);
  const [status, setStatus] = useState("Iniciando desconexión...");
  const [isComplete, setIsComplete] = useState(false);
  const [hasError, setHasError] = useState(false);

  useEffect(() => {
    if (open) {
      // Resetear estado al abrir
      setProgress(0);
      setIsComplete(false);
      setHasError(false);
      setStatus("Iniciando desconexión...");
      
      // Simular progreso de desconexión
      const progressInterval = setInterval(() => {
        setProgress((prevProgress) => {
          if (prevProgress >= 100) {
            clearInterval(progressInterval);
            setIsComplete(true);
            setStatus("¡Desconexión completada exitosamente!");
            
            // ✅ ACTUALIZAR PÁGINA AUTOMÁTICAMENTE DESPUÉS DE COMPLETAR
            setTimeout(() => {
              console.log("🔄 Actualizando página después de desconexión...");
              window.location.reload();
            }, 2000);
            
            return 100;
          }
          
          // Actualizar progreso gradualmente
          const increment = Math.random() * 20 + 10; // Entre 10 y 30 (más rápido que reconexión)
          const newProgress = Math.min(prevProgress + increment, 100);
          
          // Actualizar mensaje según progreso
          if (newProgress < 25) {
            setStatus("Cerrando sesión de WhatsApp...");
          } else if (newProgress < 50) {
            setStatus("Finalizando conexión...");
          } else if (newProgress < 75) {
            setStatus("Limpiando datos de sesión...");
          } else {
            setStatus("Completando desconexión...");
          }
          
          return newProgress;
        });
      }, 600); // Actualizar cada 600ms (más rápido que reconexión)

      return () => {
        clearInterval(progressInterval);
      };
    }
  }, [open]);

  const handleClose = (event, reason) => {
    // ✅ NUEVA API: Filtrar eventos de backdrop click cuando no se puede cerrar
    if (reason === 'backdropClick' && !isComplete && !hasError) {
      return; // No cerrar si está en proceso
    }
    
    if (isComplete || hasError) {
      onClose();
    }
  };

  const getStatusIcon = () => {
    if (hasError) {
      return <CircularProgress className={classes.errorIcon} />;
    }
    if (isComplete) {
      return <CircularProgress className={classes.successIcon} />;
    }
    return <Close className={classes.disconnectIcon} />;
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      className={classes.dialog}
      disableEscapeKeyDown={!isComplete && !hasError}
    >
      <DialogTitle className={classes.title}>
        Desconectando {whatsAppName}
      </DialogTitle>
      
      <DialogContent className={classes.content}>
        <Box className={classes.iconContainer}>
          {getStatusIcon()}
        </Box>
        
        <Typography className={classes.statusText}>
          {status}
        </Typography>
        
        <Box className={classes.progressContainer}>
          <LinearProgress
            variant="determinate"
            value={progress}
            className={classes.progressBar}
          />
          <Typography className={classes.progressText}>
            {Math.round(progress)}% completado
          </Typography>
        </Box>
        
        {isComplete && (
          <Typography 
            style={{ 
              color: "#4caf50", 
              fontWeight: "bold", 
              marginTop: "16px" 
            }}
          >
            <span role="img" aria-label="checkmark">✅</span> Actualizando página automáticamente...
          </Typography>
        )}
      </DialogContent>
      
      <DialogActions className={classes.actions}>
        {(isComplete || hasError) && (
          <Button
            onClick={handleClose}
            variant="contained"
            className={classes.closeButton}
          >
            {isComplete ? "Cerrar" : "Entendido"}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default DisconnectProgressModal;
